import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";

import MagneticButton from "@/components/MagneticButton";
import TiltCard from "@/components/TiltCard";
import Marquee from "@/components/Marquee";
import { BRAND, IMG, SERVICES, TESTIMONIALS, PROCESS, PAGE_CONTENT } from "@/lib/constants";

function useReveal() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("in")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

export default function Home() {
  useReveal();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 800], [1, 1.1]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0]);

  const [stats, setStats] = useState({ events_covered: 250, client_satisfaction: 98 });
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/stats`)
      .then((r) => r.ok && r.json())
      .then((d) => d && setStats(d))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* ---------------- HERO ---------------- */}
      <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
        <motion.div style={{ y: heroY, scale: heroScale }} className="absolute inset-0">
          <img src={IMG.hero} alt="Hero" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
          <div className="absolute inset-0 noise-overlay" />
        </motion.div>

        <motion.div style={{ opacity: heroOpacity }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pb-24 pt-40 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.9 }}
            className="flex items-center gap-4 mb-8"
          >
            <span className="h-px w-16 bg-copper" />
            <span className="font-display text-[11px] uppercase tracking-[0.4em] text-copper">{PAGE_CONTENT.home.hero.kicker}</span>
          </motion.div>

          <h1 className="font-serif text-[clamp(3rem,9vw,10rem)] leading-[0.95] tracking-tighter max-w-5xl">
            <motion.span initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.35, duration: 1 }} className="block">
              {PAGE_CONTENT.home.hero.title1}
            </motion.span>
            <motion.span initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 1 }} className="block">
              {PAGE_CONTENT.home.hero.title2} <em className="text-copper font-normal not-italic"><span className="italic font-light">{PAGE_CONTENT.home.hero.title3}</span></em> {PAGE_CONTENT.home.hero.title4}
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 1 }}
            className="mt-10 max-w-xl text-lg font-light text-parchment/75 leading-relaxed"
          >
            {PAGE_CONTENT.home.hero.desc}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-5 items-center"
          >
            <MagneticButton as="a" href="/services" testId="hero-cta-services">
              Explore Services <ArrowUpRight size={14} />
            </MagneticButton>
            <MagneticButton as="a" href="/contact" variant="ghost" testId="hero-cta-book">
              Book a Consultation <ArrowRight size={14} />
            </MagneticButton>
          </motion.div>

          <div className="mt-24 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-12 max-w-3xl">
            {[
              { k: "Many", l: "Events Crafted" },
              { k: stats.client_satisfaction + "%", l: "Client Love" },
              { k: "07", l: "Signature Services" },
              { k: "5★", l: "Rated Service" },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1 + i * 0.1, duration: 0.6 }}
                className="border-l border-copper/40 pl-5"
              >
                <div className="font-serif text-4xl md:text-5xl text-parchment">{s.k}</div>
                <div className="font-display text-[10px] uppercase tracking-[0.3em] text-muted2 mt-1">{s.l}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-display text-[10px] tracking-[0.4em] uppercase text-parchment/50 z-10">
          Scroll ↓
        </div>
      </section>

      {/* ---------------- MARQUEE ---------------- */}
      <Marquee items={SERVICES.map((s) => s.title)} />

      {/* ---------------- SERVICES ---------------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="services-section">
        <div className="flex items-end justify-between flex-wrap gap-8 mb-20 reveal">
          <div>
            <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-4">{PAGE_CONTENT.home.services.kicker}</div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight max-w-3xl">
              {PAGE_CONTENT.home.services.title1} <em className="font-light italic text-copper">{PAGE_CONTENT.home.services.title2}</em>
            </h2>
          </div>
          <Link to="/services" data-testid="services-all-link" className="link-underline font-display text-[11px] uppercase tracking-[0.3em] text-parchment flex items-center gap-2">
            View All Services <ArrowUpRight size={14} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 auto-rows-[400px]">
          {SERVICES.map((s, i) => {
            let spanClasses = "";
            if (i === 0) spanClasses = "md:col-span-2 md:row-span-2";
            else if (i === 1) spanClasses = "md:col-span-1 md:row-span-1";
            else if (i === 2) spanClasses = "md:col-span-1 md:row-span-1";
            else if (i === 3) spanClasses = "md:col-span-1 md:row-span-1";
            else if (i === 4) spanClasses = "md:col-span-2 md:row-span-1";
            else if (i === 5) spanClasses = "md:col-span-1 md:row-span-1";
            else if (i === 6) spanClasses = "md:col-span-2 md:row-span-1";

            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 30, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`service-bento-${s.slug}`}
                className={`relative group overflow-hidden border border-line bg-ink hover:border-copper/50 transition-colors duration-700 ${spanClasses}`}
              >
                <Link to={`/services#${s.slug}`} className="block w-full h-full">
                  <div className="absolute inset-0">
                    <img src={s.img} alt={s.title} className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-105 opacity-60 group-hover:opacity-40" />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/90 to-transparent" />
                  </div>
                  
                  <div className="absolute inset-0 p-8 md:p-10 flex flex-col justify-end">
                    <div className="absolute top-8 left-8 font-display text-[10px] tracking-[0.3em] uppercase text-copper opacity-0 group-hover:opacity-100 transition-opacity duration-500 translate-y-[-10px] group-hover:translate-y-0">
                      {s.num} · {s.kicker}
                    </div>
                    
                    <div className="relative z-10 transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 ease-[0.22,1,0.36,1]">
                      <h3 className={`font-serif text-parchment tracking-tight mb-4 ${i === 0 ? "text-5xl md:text-7xl" : "text-4xl"}`}>
                        {s.title}
                      </h3>
                      
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100 h-0 group-hover:h-auto overflow-hidden">
                        <p className={`text-parchment/70 leading-relaxed mb-6 ${i === 0 ? "text-lg max-w-lg" : "text-sm"}`}>
                          {s.short}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {s.tags.slice(0, 3).map((t) => (
                            <span key={t} className="text-[9px] uppercase tracking-[0.25em] font-display px-3 py-1.5 border border-copper/30 text-copper bg-ink/50 backdrop-blur-md rounded-full">
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="absolute bottom-8 right-8 w-12 h-12 rounded-full border border-line bg-ink/50 backdrop-blur-md flex items-center justify-center text-copper opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110">
                      <ArrowUpRight size={20} />
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ---------------- WHY US ---------------- */}
      <section className="relative py-32 overflow-hidden" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 reveal">
            <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-4">{PAGE_CONTENT.home.whyUs.kicker}</div>
            <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight mb-8">
              {PAGE_CONTENT.home.whyUs.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.home.whyUs.title2}</em>
            </h2>
            <p className="text-parchment/70 leading-relaxed">
              {PAGE_CONTENT.home.whyUs.desc}
            </p>

            <div className="mt-10 aspect-[4/5] overflow-hidden border border-line">
              <img src={IMG.g2} alt="event" className="w-full h-full object-cover kenburns-img" />
            </div>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-6">
            {PAGE_CONTENT.home.whyUs.features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className="p-8 bg-surface border border-line hover:border-copper/50 transition-colors duration-500 group"
              >
                <div className="font-serif text-5xl text-copper mb-6 transition-transform duration-500 group-hover:translate-x-2">{f.s}</div>
                <h3 className="font-serif text-2xl mb-3 tracking-tight">{f.t}</h3>
                <p className="text-sm text-parchment/70 leading-relaxed">{f.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- PROCESS ---------------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="process-section">
        <div className="reveal mb-20">
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-4">{PAGE_CONTENT.home.process.kicker}</div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            {PAGE_CONTENT.home.process.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.home.process.title2}</em>
          </h2>
        </div>
        <div className="grid md:grid-cols-4 gap-6 md:gap-2">
          {PROCESS.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.8 }}
              className="relative p-8 border-t border-copper/30"
            >
              <div className="absolute top-0 left-0 h-px w-8 bg-copper -translate-y-px" />
              <div className="font-serif text-copper text-5xl mb-6">{p.r}</div>
              <h3 className="font-serif text-2xl mb-3 tracking-tight">{p.t}</h3>
              <p className="text-sm text-parchment/70">{p.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- GALLERY STRIP ---------------- */}
      <section className="py-20 overflow-hidden" data-testid="gallery-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-end justify-between mb-10">
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper">{PAGE_CONTENT.home.galleryStrip.kicker}</div>
          <Link to="/gallery" data-testid="gallery-link" className="link-underline font-display text-[11px] uppercase tracking-[0.3em] flex items-center gap-2">
            Full Gallery <ArrowUpRight size={14} />
          </Link>
        </div>
        <div className="flex gap-4 overflow-hidden">
          <div className="flex gap-4 marquee-track">
            {[IMG.g1, IMG.g2, IMG.g3, IMG.g4, IMG.g5, IMG.g6, IMG.g1, IMG.g2, IMG.g3, IMG.g4, IMG.g5, IMG.g6].map((src, i) => (
              <div key={i} className="w-[22rem] h-[28rem] flex-shrink-0 overflow-hidden">
                <img src={src} alt="gallery" className="w-full h-full object-cover kenburns-img" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="testimonials-section">
        <div className="mb-20 reveal">
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-4">{PAGE_CONTENT.home.testimonials.kicker}</div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tight">
            {PAGE_CONTENT.home.testimonials.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.home.testimonials.title2}</em>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-8 lg:gap-14">
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.8 }}
              className="border-t border-copper/30 pt-8"
            >
              <div className="text-copper font-serif text-6xl leading-none mb-4">"</div>
              <p className="font-serif italic text-2xl md:text-3xl leading-snug text-parchment mb-8">{t.q}</p>
              <div className="font-display text-[11px] uppercase tracking-[0.3em] text-parchment">{t.n}</div>
              <div className="font-display text-[10px] uppercase tracking-[0.3em] text-muted2 mt-1">{t.t}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="relative py-32 overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0">
          <img src={IMG.heroAlt} alt="" className="w-full h-full object-cover kenburns-img opacity-40" />
          <div className="absolute inset-0 bg-ink/80" />
        </div>
        <div className="relative max-w-4xl mx-auto text-center px-6">
          <div className="font-display text-[11px] uppercase tracking-[0.4em] text-copper mb-6">{PAGE_CONTENT.home.cta.kicker}</div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-10">
            {PAGE_CONTENT.home.cta.title1} <em className="italic font-light text-copper">{PAGE_CONTENT.home.cta.title2}</em>
          </h2>
          <div className="flex flex-wrap gap-5 justify-center">
            <MagneticButton as="a" href={`tel:${BRAND.phone}`} testId="cta-call">Call {BRAND.phoneDisplay}</MagneticButton>
            <MagneticButton as="a" href="/contact" variant="ghost" testId="cta-enquire">Send Enquiry <ArrowUpRight size={14} /></MagneticButton>
          </div>
        </div>
      </section>
    </div>
  );
}
